import React, { createContext, useCallback, useContext, useState } from "react";

const BlockStylesContext = createContext(null);

export function BlockStylesProvider({ children }) {
  const [blockStyles, setBlockStyles] = useState({});
  const [activeBlockId, setActiveBlockId] = useState(null);
  const [activeAdvancedBlockId, setActiveAdvancedBlockId] = useState(null);

  const openAdvancedBlock = useCallback((blockId) => {
    setActiveAdvancedBlockId(blockId);
  }, []);

  const closeAdvancedBlock = useCallback(() => {
    setActiveAdvancedBlockId(null);
  }, []);

  const updateBlockStyle = useCallback((blockId, updates) => {
    setBlockStyles((prev) => ({
      ...prev,
      [blockId]: { ...(prev[blockId] || {}), ...updates },
    }));
  }, []);

  const resetBlockStyle = useCallback((blockId, property) => {
    setBlockStyles((prev) => {
      const copy = { ...prev };
      if (copy[blockId]) {
        const blockCopy = { ...copy[blockId] };
        delete blockCopy[property];
        if (Object.keys(blockCopy).length === 0) {
          delete copy[blockId];
        } else {
          copy[blockId] = blockCopy;
        }
      }
      return copy;
    });
  }, []);

  const resetAllBlockStyles = useCallback((blockId) => {
    setBlockStyles((prev) => {
      const copy = { ...prev };
      delete copy[blockId];
      return copy;
    });
  }, []);

  const getResolvedStyle = useCallback((blockId, globalDesign) => {
    const overrides = blockStyles[blockId] || {};
    return {
      backgroundColor: overrides.backgroundColor ?? globalDesign.cardBg,
      textColor: overrides.textColor ?? globalDesign.textColor,
      accentColor: overrides.accentColor ?? globalDesign.accentColor,
      fontFamily: overrides.fontFamily ?? globalDesign.fontFamily,
      padding: overrides.padding ?? "22px",
      borderRadius: overrides.borderRadius ?? "14px",
    };
  }, [blockStyles]);

  const value = {
    blockStyles,
    setBlockStyles,
    activeBlockId,
    setActiveBlockId,
    activeAdvancedBlockId,
    openAdvancedBlock,
    closeAdvancedBlock,
    updateBlockStyle,
    resetBlockStyle,
    resetAllBlockStyles,
    getResolvedStyle,
  };

  return (
    <BlockStylesContext.Provider value={value}>
      {children}
    </BlockStylesContext.Provider>
  );
}

export function useBlockStyles() {
  const ctx = useContext(BlockStylesContext);
  if (!ctx) throw new Error("useBlockStyles must be used within BlockStylesProvider");
  return ctx;
}
